import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { decodeFolderPath } from '@/lib/utils';
import { MODES } from '@scio3/shared-types';

export const PERSISTED_PATH_PREFIX = 'scio3:lastPath:';
const STORAGE_PREFIX = PERSISTED_PATH_PREFIX;

/**
 * A stored `path` holds one of two kinds of value, and they are not interchangeable:
 * a REAL FOLDER PATH, which always starts with `/`, or a VIRTUAL LOCATION token
 * (`MODES.TRASH` = `trash`, `MODES.SHARE_WITH_ME`, `MODES.FAVORITES`), which never does.
 *
 * Every surface that BROWSES a folder must accept only the first kind. A virtual token used as a
 * folder path matches no Group, so that surface's "folder was deleted" validation fires and resets
 * it to root — and because the Assets page, AssetPickerPanel and SelectAssetModal all share this
 * one `assets` entry, the reset clears the page's saved location on its way out. That is #2474:
 * Files/Assets returned to Home instead of Trash after a visit to the playlist editor.
 *
 * Root (`/`) is excluded too — every call site treats root as "nothing to restore".
 */
export function isFolderPath(path: string | null | undefined): path is string {
  return typeof path === 'string' && path.startsWith('/') && path !== '/';
}

/** Custom event dispatched when any persisted path changes in sessionStorage.
 *  Allows external subscribers (e.g., AssetPickerPanel's useSyncExternalStore)
 *  to detect writes from other pages (e.g., Assets page) within the same tab. */
export const PERSISTED_PATH_CHANGE_EVENT = 'scio3:persisted-path-change';

function dispatchPathChange() {
  window.dispatchEvent(new Event(PERSISTED_PATH_CHANGE_EVENT));
}

// Module-level state: survives SPA navigation, cleared on full page reload.
// This is the restore source — so manually clearing the URL bar (full reload)
// correctly lands at root, while SPA tab switches still restore the folder.
const savedPaths: Record<string, Record<string, string> | undefined> = {};

// Tracks pages where usePersistedPath restored params (including id) on mount.
// useDeepLinkRestore checks this to skip its own setSearchParams call, avoiding
// a race where two competing setSearchParams calls cause the path to be lost.
export const restoredPages: Set<string> = new Set();

/**
 * Persists the current folder path per page across SPA tab switches.
 * When user returns to a page with no path in URL, restores the last-visited path.
 *
 * Uses module-level state for restore (cleared on full page reload) and
 * sessionStorage for external consumers (e.g., useCanvasIframe reads assets path).
 *
 * This fixes the issue where switching tabs loses the folder position (#2755).
 *
 * @param pageKey - Unique key for the page (e.g., 'assets', 'screens')
 * @returns `true` while a stored path is still on its way into the URL. Pages that auto-select a row
 *   must skip while this is set. Otherwise they select on the FIRST render, when the URL is still at
 *   root and the root list is warm from the idle prefetch — picking an item from outside the folder
 *   the user is about to be restored into. That one does not self-correct: folder navigation is
 *   sticky, so the out-of-folder item stays in the panel (#2151).
 *
 *   Seeded SYNCHRONOUSLY, during the first render. The restore happens in the mount effect below and
 *   its `setSearchParams` lands a commit later still, so anything derived from an effect would be
 *   too late to close the window it is meant to close. Same rule as `useDeepLinkRestore`.
 */
export function usePersistedPath(pageKey: string): boolean {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawPath = searchParams.get('path');
  // Decode scio1 tilde-encoding (~2F → /) so all consumers get clean paths
  const currentPath = rawPath ? decodeFolderPath(rawPath) : null;
  const teamId = searchParams.get('teamId');
  const shared = searchParams.get('shared');
  const view = searchParams.get('view');
  const id = searchParams.get('id');
  const prevPathRef = useRef<string | null>(null);
  const prevViewRef = useRef<string | null>(null);

  // Is a restore coming? Decided once, on the first render, from the same three inputs the mount
  // effect below uses — so a consumer gating on it is never released onto a pre-restore URL.
  // `undefined` = not computed yet; `null` = nothing to restore; a string = that path is on its way.
  const pendingRestoreRef = useRef<string | null | undefined>(undefined);
  if (pendingRestoreRef.current === undefined) {
    const hasDeepLink =
      searchParams.get('id') || searchParams.get('editId') || searchParams.get('feed_id');
    if (currentPath || hasDeepLink) {
      pendingRestoreRef.current = null;
    } else {
      const stored =
        savedPaths[pageKey] ??
        (() => {
          try {
            const raw = sessionStorage.getItem(STORAGE_PREFIX + pageKey);
            return raw ? (JSON.parse(raw) as Record<string, string>) : null;
          } catch {
            return null;
          }
        })();
      pendingRestoreRef.current = stored?.path ?? null;
    }
  }
  // Released once the URL has ANY path — deliberately not an equality check against the pending
  // value: if the user navigates elsewhere mid-restore we must still release, not stall the page on
  // an empty panel forever.
  //
  // The release is a ONE-SHOT: consume the pending value the first time a path shows up, so the
  // flag can never re-arm. `pendingRestoreRef` records a decision made on the FIRST render ("a
  // restore is on its way"), while the flag below is recomputed on EVERY render — so without this,
  // any later navigation back to root in the same mount made `currentPath === null` true again and
  // re-raised a flag whose restore had landed long ago. That permanently suppressed the consumer's
  // auto-select, because nothing re-reads this ref. Production repro (#2476): Schedules → Trash →
  // Screens → Schedules (restores `?path=trash`) → sidebar Home, which calls `setSearchParams({})`
  // when nothing is selected. The schedule list loaded and the panel stayed empty; a full reload
  // "fixed" it only because that clears `savedPaths`, so no restore was pending to begin with.
  if (pendingRestoreRef.current !== null && currentPath !== null) {
    pendingRestoreRef.current = null;
  }
  const restorePending = pendingRestoreRef.current !== null && currentPath === null;

  // On mount: if no path in URL and no deep-link params, restore from module-level state
  useEffect(() => {
    // Skip if URL already has a path (direct navigation, deep link, refresh)
    if (currentPath) return;

    // Skip if URL has deep-link params (id, editId, feed_id) — those are intentional
    if (searchParams.get('id') || searchParams.get('editId') || searchParams.get('feed_id')) return;

    // Primary: module-level state (survives SPA tab switches)
    // Fallback: sessionStorage (survives page reload — restores folder context
    // so users don't lose their position after refresh)
    const stored =
      savedPaths[pageKey] ??
      (() => {
        try {
          const raw = sessionStorage.getItem(STORAGE_PREFIX + pageKey);
          return raw ? (JSON.parse(raw) as Record<string, string>) : null;
        } catch {
          return null;
        }
      })();
    if (stored) {
      // Defer deletion so StrictMode's second mount invocation still sees the
      // same value instead of falling through to stale sessionStorage (#892).
      setTimeout(() => {
        delete savedPaths[pageKey];
      }, 0);
      // Signal to useDeepLinkRestore to skip its own setSearchParams call —
      // restoring path+id in one call avoids the race where two competing
      // setSearchParams calls cause the second to overwrite the first.
      restoredPages.add(pageKey);
      setSearchParams(stored, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // On path change: save to both module-level state and sessionStorage.
  // Module-level state is the restore source (SPA tab switches).
  // sessionStorage is kept for external consumers (useCanvasIframe reads it directly).
  //
  // Persist teamId and shared alongside path so cross-team shared folders
  // restore correctly when returning from another tab (#285).
  //
  // Uses prevPathRef to track transitions: only clear when going
  // FROM a valid path to null/root (explicit root navigation). Mount-time
  // null→null transitions are ignored, so StrictMode double-fire won't
  // wipe the stored path (#311, #281).
  //
  // No suppress/barrier needed: effect bodies don't fire on unmount (only cleanup
  // functions do), so fullLogout's clearPersistedPaths() can't be undone.
  useEffect(() => {
    const prev = prevPathRef.current;
    const prevView = prevViewRef.current;
    prevPathRef.current = currentPath;
    prevViewRef.current = view;

    if (currentPath && currentPath !== '/') {
      const params: Record<string, string> = { path: currentPath };
      if (teamId) params.teamId = teamId;
      if (shared) params.shared = shared;
      if (view) params.view = view;
      if (id) params.id = id;
      // Always save to module-level state (SPA tab switches restore it)
      savedPaths[pageKey] = params;
      // Save to sessionStorage for real folder paths only — SHARE_WITH_ME and
      // FAVORITES are navigation modes, not folder positions. Full page reload
      // should land at root, not force the user back into that virtual location.
      if (currentPath !== MODES.SHARE_WITH_ME && currentPath !== MODES.FAVORITES) {
        const storageParams = { ...params };
        delete storageParams.id;
        sessionStorage.setItem(STORAGE_PREFIX + pageKey, JSON.stringify(storageParams));
        dispatchPathChange();
      }
    } else if (view) {
      // Persist view-only state (e.g. Engage ?view=recent) even without a path (#606)
      const params = { view };
      savedPaths[pageKey] = params;
      sessionStorage.setItem(STORAGE_PREFIX + pageKey, JSON.stringify(params));
      dispatchPathChange();
    } else if ((prev && prev !== '/') || prevView) {
      // Transitioning from a valid path or view to root/home — clear stored state.
      // This ensures navigating to root is remembered, while mount-time
      // null→null transitions don't wipe the stored path.
      delete savedPaths[pageKey];
      sessionStorage.removeItem(STORAGE_PREFIX + pageKey);
      dispatchPathChange();
    }
  }, [currentPath, pageKey, teamId, shared, view, id]);

  return restorePending;
}

/**
 * Updates persisted path from external consumers (AssetPickerPanel, SelectAssetModal).
 * Merges with existing state so callers that only know {path} don't wipe {shared, teamId}.
 * Writes to BOTH module-level savedPaths and sessionStorage so the Assets page
 * restores correctly regardless of whether the module was reloaded (HMR) or not (prod).
 */
export function setPersistedPath(pageKey: string, params: Record<string, string>) {
  const existing = savedPaths[pageKey];
  const merged = existing ? { ...existing, ...params } : params;
  // External callers should not persist `id` — it's a deep-link param, not folder context
  delete merged.id;
  // Clear stale cross-team context when caller doesn't provide them —
  // prevents shared folder's teamId/shared leaking into own-team folder restores
  if (!params.teamId) delete merged.teamId;
  if (!params.shared) delete merged.shared;
  savedPaths[pageKey] = merged;
  sessionStorage.setItem(STORAGE_PREFIX + pageKey, JSON.stringify(merged));
  dispatchPathChange();
}

/** Clears persisted path for a single page (module-level + sessionStorage). */
export function clearPersistedPath(pageKey: string) {
  delete savedPaths[pageKey];
  sessionStorage.removeItem(STORAGE_PREFIX + pageKey);
  dispatchPathChange();
}

/** Clears all persisted folder paths (module-level + sessionStorage). Called on logout and team switch. */
export const clearPersistedPaths = () => {
  // Clear module-level state (survives SPA navigation but must not survive logout/team-switch)
  for (const key of Object.keys(savedPaths)) delete savedPaths[key];
  restoredPages.clear();
  // Clear sessionStorage
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith(STORAGE_PREFIX))
    .forEach((k) => sessionStorage.removeItem(k));
};
